import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { planRank, UPGRADE_PLAN_ORDER, formatPlanPrice } from "./DealerPlanTierCard";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency } from "../../intl";
import { requestDealerPlanUpgrade } from "../../services/automapDealerPlanApi";

const UPGRADE_REASONS = [
  "more_listings",
  "more_leads",
  "featured_placement",
  "performance_reports",
  "bulk_upload",
  "priority_support",
];

export default function DealerPlanUpgradeModal({
  isOpen,
  dealerId,
  currentPlanKey,
  comparisonPlans = [],
  initialTargetPlanKey = "",
  pendingRequest = null,
  onClose,
  onSubmitted,
}) {
  const { t, formatPlanLabel, formatPlanFeature, formatReportsAccess, translateError } = useDealerPortalIntl();
  const { formatMoney } = useCurrency();
  const [step, setStep] = useState("plan");
  const [targetPlanKey, setTargetPlanKey] = useState(initialTargetPlanKey || "");
  const [reasons, setReasons] = useState([]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const upgradeOptions = useMemo(() => {
    const currentRank = planRank(currentPlanKey);
    return UPGRADE_PLAN_ORDER.map((key) => comparisonPlans.find((plan) => plan.planKey === key))
      .filter(Boolean)
      .filter((plan) => planRank(plan.planKey) > currentRank);
  }, [comparisonPlans, currentPlanKey]);

  const targetPlan = useMemo(
    () => comparisonPlans.find((plan) => plan.planKey === targetPlanKey) || null,
    [comparisonPlans, targetPlanKey]
  );

  const currentPlan = useMemo(
    () => comparisonPlans.find((plan) => plan.planKey === currentPlanKey) || null,
    [comparisonPlans, currentPlanKey]
  );

  useEffect(() => {
    if (!isOpen) return;
    setStep("plan");
    setTargetPlanKey(initialTargetPlanKey || upgradeOptions[0]?.planKey || "");
    setReasons([]);
    setMessage("");
    setSubmitting(false);
    setError("");
  }, [isOpen, initialTargetPlanKey, upgradeOptions]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleReason = (reason) => {
    setReasons((current) =>
      current.includes(reason) ? current.filter((item) => item !== reason) : [...current, reason]
    );
  };

  const submitRequest = async () => {
    if (!targetPlanKey) {
      setError(t("plan.upgradeSelectPlanRequired"));
      return;
    }
    if (pendingRequest?.targetPlanKey === targetPlanKey) {
      setError(translateError("", "submitUpgrade", "upgrade_pending"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await requestDealerPlanUpgrade(dealerId, {
        targetPlanKey,
        reasons,
        message,
      });
      setStep("success");
      onSubmitted?.(result.request);
    } catch (err) {
      setError(translateError(err.message, "submitUpgrade", err.code, err.status));
    } finally {
      setSubmitting(false);
    }
  };

  const renderFeatureDelta = (label, currentValue, nextValue) => {
    if (currentValue === nextValue) return null;
    let currentDisplay = formatPlanFeature(currentValue);
    let nextDisplay = formatPlanFeature(nextValue);
    if (currentValue === "basic" || currentValue === "full" || nextValue === "basic" || nextValue === "full") {
      currentDisplay = formatReportsAccess(currentValue);
      nextDisplay = formatReportsAccess(nextValue);
    }
    return (
      <li key={label}>
        <span>{label}</span>
        <strong>
          {currentDisplay} → {nextDisplay}
        </strong>
      </li>
    );
  };

  return createPortal(
    <div className="dealer-plan-upgrade-modal" role="presentation" onClick={onClose}>
      <div
        className="dealer-plan-upgrade-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dealer-plan-upgrade-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dealer-plan-upgrade-modal__header">
          <div>
            <p className="dealer-plan-upgrade-modal__eyebrow">{t("plan.upgradeModalEyebrow")}</p>
            <h2 id="dealer-plan-upgrade-title">{t("plan.upgradeModalTitle")}</h2>
          </div>
          <button type="button" className="dealer-plan-upgrade-modal__close" onClick={onClose} aria-label={t("common.close")}>
            ×
          </button>
        </header>

        {pendingRequest && step !== "success" ? (
          <div className="dealer-plan-upgrade-modal__pending">
            <strong>{t("plan.upgradePendingTitle")}</strong>
            <p>
              {t("plan.upgradePendingBody", {
                plan: formatPlanLabel(pendingRequest.targetPlanKey),
              })}
            </p>
          </div>
        ) : null}

        {step === "plan" ? (
          <div className="dealer-plan-upgrade-modal__body">
            <p className="muted">{t("plan.upgradeModalPlanIntro")}</p>

            {upgradeOptions.length === 0 ? (
              <p className="dealer-state-box">{t("plan.topTierBody")}</p>
            ) : (
              <>
                <div className="dealer-plan-upgrade-modal__plan-grid">
                  {upgradeOptions.map((plan) => {
                    const selected = targetPlanKey === plan.planKey;
                    return (
                      <button
                        key={plan.planKey}
                        type="button"
                        className={`dealer-plan-upgrade-modal__plan-card${selected ? " is-selected" : ""}`}
                        onClick={() => setTargetPlanKey(plan.planKey)}
                      >
                        <span className="dealer-plan-upgrade-modal__plan-emoji" aria-hidden="true">
                          {t(`plan.planEmojis.${plan.planKey}`)}
                        </span>
                        <strong>{formatPlanLabel(plan.planKey, plan.publicName)}</strong>
                        <span className="muted">{formatPlanPrice(plan, formatMoney, t)}</span>
                        <span className="dealer-plan-upgrade-modal__plan-desc">{plan.description}</span>
                      </button>
                    );
                  })}
                </div>

                {targetPlan && currentPlan ? (
                  <div className="dealer-plan-upgrade-modal__comparison">
                    <h3>{t("plan.upgradeComparisonTitle")}</h3>
                    <ul>
                      {renderFeatureDelta(t("plan.colListings"), currentPlan.listingLimit, targetPlan.listingLimit)}
                      {renderFeatureDelta(t("plan.colLeads"), currentPlan.leadLimitMonthly, targetPlan.leadLimitMonthly)}
                      {renderFeatureDelta(t("plan.colBulk"), currentPlan.bulkUploadAccess, targetPlan.bulkUploadAccess)}
                      {renderFeatureDelta(
                        t("plan.colReports"),
                        currentPlan.performanceReportsAccess,
                        targetPlan.performanceReportsAccess
                      )}
                      {renderFeatureDelta(
                        t("plan.colFeatured"),
                        currentPlan.featuredPlacementEligible,
                        targetPlan.featuredPlacementEligible
                      )}
                    </ul>
                  </div>
                ) : null}
              </>
            )}

            <footer className="dealer-plan-upgrade-modal__footer">
              <button type="button" className="dealer-portal-btn" onClick={onClose}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="dealer-portal-btn primary"
                disabled={!targetPlanKey}
                onClick={() => setStep("details")}
              >
                {t("plan.upgradeContinue")}
              </button>
            </footer>
          </div>
        ) : null}

        {step === "details" ? (
          <div className="dealer-plan-upgrade-modal__body">
            <p className="muted">{t("plan.upgradeReasonsIntro")}</p>
            <div className="dealer-plan-upgrade-modal__reasons">
              {UPGRADE_REASONS.map((reason) => (
                <label key={reason} className="dealer-plan-upgrade-modal__reason">
                  <input
                    type="checkbox"
                    checked={reasons.includes(reason)}
                    onChange={() => toggleReason(reason)}
                  />
                  <span>{t(`plan.upgradeReasons.${reason}`)}</span>
                </label>
              ))}
            </div>
            <label className="dealer-plan-upgrade-modal__message">
              <span>{t("plan.upgradeMessageLabel")}</span>
              <textarea
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={t("plan.upgradeMessagePlaceholder")}
              />
            </label>
            {error ? <p className="dealer-plan-upgrade-modal__error">{error}</p> : null}
            <footer className="dealer-plan-upgrade-modal__footer">
              <button type="button" className="dealer-portal-btn" onClick={() => setStep("plan")}>
                {t("plan.upgradeBack")}
              </button>
              <button type="button" className="dealer-portal-btn primary" onClick={() => setStep("review")}>
                {t("plan.upgradeContinue")}
              </button>
            </footer>
          </div>
        ) : null}

        {step === "review" ? (
          <div className="dealer-plan-upgrade-modal__body">
            <div className="dealer-plan-upgrade-modal__review">
              <div>
                <span className="muted">{t("plan.currentPlanBadge")}</span>
                <strong>{formatPlanLabel(currentPlanKey, currentPlan?.publicName)}</strong>
              </div>
              <div>
                <span className="muted">{t("plan.upgradeTargetLabel")}</span>
                <strong>{formatPlanLabel(targetPlanKey, targetPlan?.publicName)}</strong>
              </div>
              <div>
                <span className="muted">{t("plan.upgradeEstimatedPrice")}</span>
                <strong>{targetPlan ? formatPlanPrice(targetPlan, formatMoney, t) : "—"}</strong>
              </div>
            </div>
            {reasons.length > 0 ? (
              <ul className="dealer-plan-upgrade-modal__review-reasons">
                {reasons.map((reason) => (
                  <li key={reason}>{t(`plan.upgradeReasons.${reason}`)}</li>
                ))}
              </ul>
            ) : null}
            {message ? <p className="dealer-plan-upgrade-modal__review-message">{message}</p> : null}
            <p className="muted">{t("plan.upgradeReviewNote")}</p>
            {error ? <p className="dealer-plan-upgrade-modal__error">{error}</p> : null}
            <footer className="dealer-plan-upgrade-modal__footer">
              <button type="button" className="dealer-portal-btn" onClick={() => setStep("details")}>
                {t("plan.upgradeBack")}
              </button>
              <button type="button" className="dealer-portal-btn primary" onClick={submitRequest} disabled={submitting}>
                {submitting ? t("plan.upgradeSubmitting") : t("plan.upgradeSubmit")}
              </button>
            </footer>
          </div>
        ) : null}

        {step === "success" ? (
          <div className="dealer-plan-upgrade-modal__body dealer-plan-upgrade-modal__body--success">
            <div className="dealer-plan-upgrade-modal__success-icon" aria-hidden="true">
              ✓
            </div>
            <h3>{t("plan.upgradeSuccessTitle")}</h3>
            <p>{t("plan.upgradeSuccessBody")}</p>
            <ol className="dealer-plan-upgrade-modal__success-steps">
              <li>{t("plan.upgradeSuccessStep1")}</li>
              <li>{t("plan.upgradeSuccessStep2")}</li>
              <li>{t("plan.upgradeSuccessStep3")}</li>
            </ol>
            <footer className="dealer-plan-upgrade-modal__footer">
              <button type="button" className="dealer-portal-btn primary" onClick={onClose}>
                {t("plan.upgradeDone")}
              </button>
            </footer>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
